import {useEffect} from "react";
import {updateBingoFeed, updateDFBingoFeed} from "Redux/actions/bingo";
import {useDispatch, useSelector} from "react-redux";
import {useRedPill} from "Hooks/api/useRedPill";
import {DF_ROOMS_ROOM_SETTINGS, IS_DF_BINGO_ENABLED, USE_DRAGONFISH_BINGO_FEED} from "Setup/config";
import extendedDayjs from "Helpers/dayjs";

const useBingo = () => {
    let timer = null;
    let dfTimer = null;
    const dispatch = useDispatch();
    const MIN_REFRESH_INTERVAL = 10;
    const MAX_REFRESH_INTERVAL = 60;
    const getBingoRoomsPath = "bingoRoomsV3";
    const getDFBingoRoomsPath = "/api/bingo/rooms/dragonfish";
    const {execute} = useRedPill(getBingoRoomsPath);
    const {execute: executeDF} = useRedPill("", "GET", getDFBingoRoomsPath);

    const bingoLoaded = useSelector((state) => state?.bingo?.isLoaded)
    const roomFeed = useSelector((state) => state?.bingo?.rooms)
    const dfRooms = useSelector((state) => state?.bingo?.dfRooms?.rooms)
    const dfRoomsLoaded = useSelector((state) => state?.bingo?.dfRooms?.isLoaded)
    const specialsFeed = useSelector((state) => state?.bingo?.specials)
    const roomDictionary = useSelector((state) => state?.bingo?.dictionary)

    useEffect(() => {
        return () => {
            if (timer) clearTimeout(timer);
            if (dfTimer) clearTimeout(dfTimer);
        };
    }, []);

    const fetchAllBingoRooms = async (singleUse = false) => {
        await Promise.all([
            fetchBingoRooms(singleUse),
            fetchDFRoomsFeed(singleUse)
        ]);
    };

    const fetchBingoRooms = async (singleUse = false) => {
        try {
            if (timer) clearTimeout(timer);
            const data = await execute();

            if (data.code === 200) {
                const bingoRooms = data.data.feed;
                let normalRooms = [];
                let specialRooms = [];
                let roomDictionary = {};

                let nextRefresh = MAX_REFRESH_INTERVAL;

                bingoRooms.forEach((room) => {
                    if (room.nextGameSec < nextRefresh) {
                        nextRefresh = room.nextGameSec;
                    }

                    room.isSpecial ? specialRooms.push(room) : normalRooms.push(room);
                    roomDictionary[room.cgid] = room.image;
                });

                specialRooms = specialRooms.sort(function (a, b) {
                    return a.nextGameSec - b.nextGameSec;
                });

                dispatch(updateBingoFeed(normalRooms, specialRooms, roomDictionary));

                if (nextRefresh < MIN_REFRESH_INTERVAL) {
                    nextRefresh = MIN_REFRESH_INTERVAL;
                }

                if (!singleUse) {
                    timer = setTimeout(() => fetchBingoRooms(), nextRefresh * 1000);
                }

                return;
            }
        } catch (e) {
            console.log(e);
        }

        if (!singleUse) {
            timer = setTimeout(() => fetchBingoRooms(), MIN_REFRESH_INTERVAL * 1000);
        }
    };

    const fetchDFRoomsFeed = async (singleUse = false) => {
        if (!IS_DF_BINGO_ENABLED) return false;

        if (!USE_DRAGONFISH_BINGO_FEED) {
            const v = DF_ROOMS_ROOM_SETTINGS.map(room => {
                console.log(room);

                if (room.openHour && room.closeHour) {
                    const UNIX_NOW = Math.floor(Date.now() / 1000);
                    const OPEN = Math.floor(new Date().setHours(room.openHour, 0, 0) / 1000);
                    const CLOSE = Math.floor(new Date().setHours(room.closeHour, 0, 0) / 1000);

                    room.open = UNIX_NOW > OPEN && UNIX_NOW < CLOSE;

                    if (!room.open) {
                        let openingDate = extendedDayjs().add(1, 'day').set('hour', room.openHour).set('minute', 0).set('second', 0).unix();
                        room.SecondsToGame = (Math.floor(openingDate)) - UNIX_NOW;
                    }
                } else {
                    room.open = true;
                }

                return room;
            }).filter(r => r.isActive);

            dispatch(updateDFBingoFeed(v));
            return;
        }

        try {
            if (dfTimer) clearTimeout(dfTimer);
            const res = await executeDF();

            if (res && res.code === 200) {
                const ACTIVE_DF_ROOMS = DF_ROOMS_ROOM_SETTINGS.filter(r => r.isActive).map(r => r.dfId);
                const dfRooms = res.data.Rooms.filter(r => ACTIVE_DF_ROOMS.includes(r.Id.toString()));

                const keysToRemove = ['__type', 'CardType', 'DisplayName', 'GameVariant', 'Id', 'LinkGameType', 'OpeningHoursDescription', 'PopulationType', 'RoomSegmentationFeedback', 'RoomSegmentationRulesCriteriaTypes', 'Status', 'ThemeID', 'ChatModerator', 'FundedChatRoomId', 'IsSpeedyGame', 'JackpotDetails', 'NonFundedChatRoomId', 'ProgressiveJackpotAmountList', 'PurchasePromotions', 'WinningEvents'];

                let nextRefresh = MAX_REFRESH_INTERVAL;

                const updatedDfRooms = DF_ROOMS_ROOM_SETTINGS.map((room) => {
                    const roomSettings = dfRooms.find(r => room.dfId === r.Id.toString());
                    room = {...room, ...roomSettings};

                    if (room.openHour && room.closeHour) {
                        const UNIX_NOW = Math.floor(Date.now() / 1000);
                        const OPEN = Math.floor(new Date().setHours(room.openHour, 0, 0) / 1000);
                        const CLOSE = Math.floor(new Date().setHours(room.closeHour, 0, 0) / 1000);

                        room.open = UNIX_NOW > OPEN && UNIX_NOW < CLOSE;

                        if (!room.open) {
                            let openingDate = extendedDayjs().add(1, 'day').set('hour', room.openHour).set('minute', 0).set('second', 0).unix();
                            room.SecondsToGame = (Math.floor(openingDate)) - UNIX_NOW;
                        }
                    } else {
                        room.open = true;
                    }

                    if (room?.JackpotDetails?.JackpotAmountList) {
                        room.JackpotAmount = room.JackpotDetails.JackpotAmountList.find(a => a.CurrencyCode === "GBP").Amount.toFixed(2);

                        if (room.Id === 4) {
                            const MIN = room.JackpotDetails.MinValue;
                            const MAX = room.JackpotDetails.MaxValue;
                            room.JackpotAmount = `${MIN} - £${MAX}`;
                        }
                    }

                    if (room?.ProgressiveJackpotAmountList) {
                        room.ProgressiveJackpotAmount = room.ProgressiveJackpotAmountList.find(a => a.CurrencyCode === "GBP").Amount.toFixed(2);
                    }

                    if (room.SecondsToGame < nextRefresh && room.SecondsToGame > MIN_REFRESH_INTERVAL) {
                        nextRefresh = room.SecondsToGame;
                    }

                    if (nextRefresh < MIN_REFRESH_INTERVAL) {
                        nextRefresh = MIN_REFRESH_INTERVAL;
                    }

                    keysToRemove.forEach(k => delete room?.[k])

                    console.log(room);
                    return room;
                });

                dispatch(updateDFBingoFeed(updatedDfRooms));

                if (!singleUse) {
                    dfTimer = setTimeout(() => fetchDFRoomsFeed(), nextRefresh * 1000);
                }
            }

            return;
        } catch (e) {
            console.log(e);
        }

        if (!dfRoomsLoaded) {
            dispatch(updateDFBingoFeed(DF_ROOMS_ROOM_SETTINGS));
        }

        if (!singleUse) {
            dfTimer = setTimeout(() => fetchDFRoomsFeed(), MIN_REFRESH_INTERVAL * 1000);
        }
    };

    const openRooms = roomFeed.filter(r => r.open);
    const closedRooms = roomFeed.filter(r => !r.open);

    const openDFRooms = dfRooms.filter(r => r.open);
    const closedDFRooms = dfRooms.filter(r => !r.open);

    const openRooms1 = openRooms.slice(0, 2);
    const openRooms2 = openRooms.slice(2, openRooms.length);

    const bingoRoomsOrder = [
        {roomType: 'PragmaticBingo', rooms: openRooms1, isActive: true},
        {roomType: 'DragonFishBingo', rooms: openDFRooms, isActive: IS_DF_BINGO_ENABLED && dfRoomsLoaded},
        {roomType: 'PragmaticBingo', rooms: openRooms2, isActive: true},
        {roomType: 'DragonFishBingo', rooms: closedDFRooms, isActive: IS_DF_BINGO_ENABLED && dfRoomsLoaded},
        {roomType: 'PragmaticBingo', rooms: closedRooms, isActive: true}
    ];

    return {
        fetchBingoRooms: fetchAllBingoRooms,
        bingoLoaded,
        roomFeed,
        dfRooms,
        dfRoomsLoaded,
        specialsFeed,
        roomDictionary,
        bingoRoomsOrder
    };
};

export default useBingo;
